import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import api, { fileUrl } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { ArrowLeft, Clock, Copy, Check, Tag } from "lucide-react";
import { toast } from "sonner";

const WORDS_PER_MIN = 220;

function readingTime(html) {
    if (!html) return 1;
    const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return Math.max(1, Math.round(text.split(" ").length / WORDS_PER_MIN));
}

function useScrollProgress() {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement;
            const h = doc.scrollHeight - doc.clientHeight;
            const p = h > 0 ? Math.min(100, Math.max(0, (doc.scrollTop / h) * 100)) : 0;
            setProgress(p);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return progress;
}

export default function BlogDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);
    const progress = useScrollProgress();

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
        window.scrollTo({ top: 0 });
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        api.get("/blog")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data?.posts || []);
                const others = list.filter((p) => p.slug !== post.slug);
                const sameCat = others.filter((p) => p.category === post.category).slice(0, 3);
                const filler = others.filter((p) => p.category !== post.category).slice(0, 3 - sameCat.length);
                setRelated([...sameCat, ...filler].slice(0, 3));
            })
            .catch(() => setRelated([]));
    }, [post]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            toast.success("Link copied.");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Couldn't copy. Long-press the URL bar instead.");
        }
    };

    if (loading) return <div className="pt-32 text-center font-editorial text-coffee/40 text-xl min-h-screen">Loading…</div>;
    if (notFound) return (
        <div className="pt-32 text-center min-h-screen">
            <div className="font-editorial text-coffee text-3xl mb-4">Not found.</div>
            <Link to="/blog" className="tracked text-[11px] text-coffee/60 gold-underline">← Back to Stories</Link>
        </div>
    );
    if (!post) return null;

    const cleanBody = DOMPurify.sanitize(renderBody(post.body), {
        USE_PROFILES: { html: true },
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src", "title"],
    });
    const readMin = readingTime(post.body);
    const dateStr = post.created_at ? new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "";
    const tags = Array.isArray(post.tags) && post.tags.length ? post.tags : (post.category ? [post.category] : []);

    return (
        <article className="min-h-screen bg-cream-light pt-24 md:pt-28 pb-20 overflow-x-hidden" data-testid="blog-detail-page">
            <SEO title={post.title} description={post.excerpt} image={fileUrl(post.cover_image)} path={`/blog/${post.slug}`} />

            {/* Scroll progress bar */}
            <div
                className="fixed top-0 left-0 right-0 z-50 h-[3px]"
                style={{ background: "rgba(26,15,0,0.08)" }}
                aria-hidden
                data-testid="scroll-progress-track"
            >
                <div
                    style={{ width: `${progress}%`, background: "#c8a96e", transition: "width 100ms linear" }}
                    className="h-full"
                    data-testid="scroll-progress-bar"
                />
            </div>

            <div className="max-w-[840px] mx-auto px-6">
                <Reveal>
                    <Link to="/blog" data-testid="back-to-stories" className="tracked text-[10px] text-coffee/60 gold-underline inline-flex items-center gap-2 mb-10">
                        <ArrowLeft size={12} /> Back to Stories
                    </Link>

                    {/* Tags row */}
                    {tags.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="blog-tags">
                            {tags.map((t, i) => (
                                <Link key={i} to={`/blog?tag=${encodeURIComponent(t)}`} className="tracked text-[10px] px-3 py-1.5 border border-gold/50 text-gold hover:bg-gold hover:text-coffee transition-colors">
                                    {t}
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* Headline */}
                    <h1 className="font-editorial font-bold text-coffee leading-[1.02] mb-6" style={{ fontSize: "clamp(2.2rem, 4.8vw, 4rem)" }}>
                        {post.title}
                    </h1>
                    {post.excerpt && (
                        <p className="font-editorial italic text-coffee/70 mb-8 leading-[1.5]" style={{ fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)" }}>
                            {post.excerpt}
                        </p>
                    )}

                    {/* Meta bar */}
                    <div className="flex items-center gap-5 flex-wrap py-4 border-y border-coffee/10 mb-10" data-testid="blog-meta">
                        <span className="tracked text-[10px] text-coffee/60">By {post.author || "Cafe Hideout"}</span>
                        {dateStr && <span className="tracked text-[10px] text-coffee/40">{dateStr}</span>}
                        <span className="inline-flex items-center gap-1.5 tracked text-[10px] text-coffee/60">
                            <Clock size={11} /> {readMin} min read
                        </span>
                        <button
                            onClick={copyLink}
                            data-testid="blog-copy-link"
                            className="ml-auto inline-flex items-center gap-2 tracked text-[10px] px-3 py-2 border border-coffee/20 text-coffee/70 hover:text-coffee hover:border-coffee transition-colors"
                        >
                            {copied ? <><Check size={12} className="text-gold" /> Copied</> : <><Copy size={12} /> Copy Link</>}
                        </button>
                    </div>
                </Reveal>

                {/* Cover image */}
                <Reveal delay={0.1}>
                    <div className="aspect-[16/9] overflow-hidden bg-coffee/10 mb-4">
                        <img src={fileUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="text-center font-editorial italic text-coffee/50 text-sm mb-14">{post.cover_caption || ""}</div>
                </Reveal>

                {/* Body */}
                <Reveal delay={0.15}>
                    <div
                        className="prose-blog font-body text-coffee/85 leading-[1.85]"
                        style={{ fontSize: "clamp(1.05rem, 1.15vw, 1.15rem)" }}
                        dangerouslySetInnerHTML={{ __html: cleanBody }}
                        data-testid="blog-body"
                    />
                </Reveal>

                {/* Footer share row */}
                <Reveal delay={0.2}>
                    <div className="mt-16 pt-8 border-t border-coffee/15 flex items-center justify-between flex-wrap gap-4">
                        <div className="tracked text-coffee/50 text-[10px]">Enjoyed this story?</div>
                        <button
                            onClick={copyLink}
                            data-testid="blog-copy-link-bottom"
                            className="inline-flex items-center gap-2 tracked text-[10px] px-5 py-3 bg-coffee text-cream hover:bg-gold hover:text-coffee transition-colors"
                        >
                            {copied ? <><Check size={12} /> Link Copied</> : <><Copy size={12} /> Copy Link</>}
                        </button>
                    </div>
                </Reveal>
            </div>

            {/* Related articles */}
            {related.length > 0 && (
                <section className="mt-24 md:mt-28 px-6 md:px-12" data-testid="related-articles">
                    <div className="max-w-[1280px] mx-auto">
                        <Reveal>
                            <div className="flex items-center gap-3 mb-10">
                                <Tag size={14} strokeWidth={1.4} className="text-gold" />
                                <div className="tracked text-coffee/60 text-[10px]">Keep Reading</div>
                                <div className="flex-1 h-px bg-coffee/15" />
                            </div>
                        </Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
                            {related.map((r, i) => (
                                <Reveal key={r.id || r.slug} delay={i * 0.06}>
                                    <Link to={`/blog/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                                        <div className="aspect-[4/3] overflow-hidden bg-coffee/5 mb-5">
                                            <img src={fileUrl(r.cover_image)} alt={r.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]" />
                                        </div>
                                        {r.category && <div className="font-mono tracked text-gold text-[10px] mb-2">{r.category}</div>}
                                        <h3 className="font-editorial font-bold text-coffee text-xl md:text-2xl leading-[1.2] mb-2 group-hover:text-gold transition-colors">{r.title}</h3>
                                        {r.excerpt && <p className="font-body text-coffee/60 text-sm leading-relaxed line-clamp-2">{r.excerpt}</p>}
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </article>
    );
}

// Render plain-text bodies as paragraphs; render HTML bodies as-is (still sanitized by caller).
function renderBody(body) {
    if (!body) return "";
    const looksHtml = /<\/?(p|h[1-6]|ul|ol|li|blockquote|strong|em|a|br|img|iframe|figure|figcaption)[\s>]/i.test(body);
    if (looksHtml) return body;
    return body.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`).join("");
}
function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
